//! Handles pointer events for data tables.

import { hasPermissionToEditFile } from '@/app/actions';
import { ContextMenuType } from '@/app/atoms/contextMenuAtom';
import { events } from '@/app/events/events';
import { sheets } from '@/app/grid/controller/Sheets';
import type { TablePointerDownResult } from '@/app/gridGL/cells/tables/Tables';
import { inlineEditorFormula } from '@/app/gridGL/HTMLGrid/inlineEditor/inlineEditorFormula';
import { inlineEditorHandler } from '@/app/gridGL/HTMLGrid/inlineEditor/inlineEditorHandler';
import { inlineEditorInput } from '@/app/gridGL/HTMLGrid/inlineEditor/inlineEditorInput';
import { doubleClickCell } from '@/app/gridGL/interaction/pointer/doubleClickCell';
import { DOUBLE_CLICK_TIME } from '@/app/gridGL/interaction/pointer/pointerUtils';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import * as gridSettings from '@/app/gridGL/pixiApp/gridSettings';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { isMobileMode } from '@/app/helpers/isMobile';
import { xyToA1 } from '@/app/quadratic-core/quadratic_core';
import type { JsRenderCodeCell } from '@/app/quadratic-core-types';
import { quadraticCore } from '@/app/web-workers/quadraticCore/quadraticCore';
import { isMac } from '@/shared/utils/isMac';
import type { FederatedPointerEvent, Point } from 'pixi.js';

// todo: dragging on double click

// Screen-pixel distance the pointer must travel after pressing a table name
// before a table move starts. Prevents the small jitter that happens during a
// click from flashing the cell-move UI. Matches PointerDown's
// MINIMUM_MOVE_POSITION. The threshold is divided by the viewport scale at
// comparison time so it stays consistent in screen pixels across zoom levels.
const TABLE_DRAG_THRESHOLD = 10;

export class PointerTable {
  cursor: string | undefined;

  private doubleClickTimeout: number | undefined;
  private doubleClickTarget:
    | { type: 'table-name' | 'column-name' | 'chart'; tableName: string; column?: number }
    | undefined;
  private tableNameDown:
    | { column: number; row: number; point: Point; downWorld: Point; table: JsRenderCodeCell }
    | undefined;

  private clearDoubleClickState = () => {
    if (this.doubleClickTimeout) {
      clearTimeout(this.doubleClickTimeout);
      this.doubleClickTimeout = undefined;
    }
    this.doubleClickTarget = undefined;
  };

  private startDoubleClickWindow = (target: {
    type: 'table-name' | 'column-name' | 'chart';
    tableName: string;
    column?: number;
  }) => {
    this.clearDoubleClickState();
    this.doubleClickTarget = target;
    this.doubleClickTimeout = window.setTimeout(() => {
      this.doubleClickTimeout = undefined;
      this.doubleClickTarget = undefined;
    }, DOUBLE_CLICK_TIME);
  };

  private isDoubleClickTarget = (target: {
    type: 'table-name' | 'column-name' | 'chart';
    tableName: string;
    column?: number;
  }) => {
    return (
      !!this.doubleClickTimeout &&
      this.doubleClickTarget?.type === target.type &&
      this.doubleClickTarget.tableName === target.tableName &&
      this.doubleClickTarget.column === target.column
    );
  };

  private getColumnName = (table: JsRenderCodeCell, columnValueIndex: number): string | undefined => {
    return table.columns.find((column) => column.valueIndex === columnValueIndex)?.name;
  };

  private pointerDownTableName = async (
    world: Point,
    tableDown: TablePointerDownResult,
    shiftKey: boolean,
    ctrlKey: boolean
  ) => {
    const target = { type: 'table-name' as const, tableName: tableDown.table.name };
    if (this.isDoubleClickTarget(target)) {
      const table = tableDown.table;
      if (table.language === 'Import') {
        events.emit('contextMenu', {
          type: ContextMenuType.Table,
          table,
          rename: true,
          column: table.x,
          row: table.y,
        });
      } else {
        const isSingleCell = table.w === 1 && table.h === 1 && !table.show_name && !table.show_columns;
        gridSettings.updateCodeEditorState((prev) => ({
          ...prev,
          diffEditorContent: undefined,
          waitingForEditorClose: {
            codeCell: {
              sheetId: sheets.current,
              pos: { x: table.x, y: table.y },
              language: table.language,
              lastModified: Number(table.last_modified),
              isSingleCell,
            },
            showCellTypeMenu: false,
            initialCode: '',
          },
        }));
      }
      this.clearDoubleClickState();
    } else {
      if (await inlineEditorHandler.handleCellPointerDown()) {
        // Check if clicked table is already selected - if so, don't change the selection
        // This allows dragging multiple selected tables together
        const selectedTableNames = sheets.sheet.cursor.getSelectedTableNames();
        const isAlreadySelected = selectedTableNames.includes(tableDown.table.name);

        if (!isAlreadySelected || shiftKey || ctrlKey) {
          sheets.sheet.cursor.selectTable(tableDown.table.name, undefined, shiftKey, ctrlKey);
        }
      } else {
        inlineEditorInput.focus();
      }

      this.startDoubleClickWindow(target);
      this.tableNameDown = {
        column: tableDown.table.x,
        row: tableDown.table.y,
        point: world,
        downWorld: world.clone(),
        table: tableDown.table,
      };
    }
  };

  private pointerDownRunCode = (tableDown: TablePointerDownResult) => {
    if (!hasPermissionToEditFile(gridSettings.getPermissions())) {
      return;
    }
    quadraticCore.rerunCodeCells(sheets.current, xyToA1(tableDown.table.x, tableDown.table.y), false);
  };

  private pointerDownDropdown = (world: Point, tableDown: TablePointerDownResult) => {
    // select the table so the menu's actions (cut/copy/run/etc.) operate on it,
    // matching the behavior of right-clicking the table
    sheets.sheet.cursor.selectTable(tableDown.table.name, undefined, false, false);
    events.emit('contextMenu', {
      type: ContextMenuType.Table,
      world,
      column: tableDown.table.x,
      row: tableDown.table.y,
      table: tableDown.table,
    });
  };

  private pointerDownColumnName = async (
    world: Point,
    tableDown: TablePointerDownResult,
    shiftKey: boolean,
    ctrlKey: boolean
  ) => {
    if (tableDown.column === undefined) {
      throw new Error('Expected column to be defined in pointerTable');
    }
    const target = { type: 'column-name' as const, tableName: tableDown.table.name, column: tableDown.column };
    if (this.isDoubleClickTarget(target)) {
      if (tableDown.table.language === 'Import') {
        events.emit('contextMenu', {
          type: ContextMenuType.TableColumn,
          world,
          column: tableDown.table.x,
          row: tableDown.table.y,
          table: tableDown.table,
          rename: true,
          selectedColumn: tableDown.column,
        });
      } else {
        const isSingleCell =
          tableDown.table.w === 1 &&
          tableDown.table.h === 1 &&
          !tableDown.table.show_name &&
          !tableDown.table.show_columns;
        gridSettings.updateCodeEditorState((prev) => ({
          ...prev,
          diffEditorContent: undefined,
          waitingForEditorClose: {
            codeCell: {
              sheetId: sheets.current,
              pos: { x: tableDown.table.x, y: tableDown.table.y },
              language: tableDown.table.language,
              lastModified: Number(tableDown.table.last_modified),
              isSingleCell,
            },
            showCellTypeMenu: false,
            initialCode: '',
          },
        }));
      }
      this.clearDoubleClickState();
    } else {
      // move cursor to column header
      if (await inlineEditorHandler.handleCellPointerDown()) {
        // don't change the selection if the mouse hasn't changed cells
        const previousPosition = pixiApp.pointer.pointerDown.previousPosition;
        const down = sheets.sheet.getColumnRowFromScreen(world.x, world.y);
        if (!previousPosition || previousPosition.x !== down.column || previousPosition.y !== down.row) {
          const columnName = this.getColumnName(tableDown.table, tableDown.column);
          if (!columnName) {
            return;
          }
          sheets.sheet.cursor.selectTable(tableDown.table.name, columnName, shiftKey, ctrlKey);
          pixiApp.pointer.pointerDown.pointerDownColumnName(world, down.column, down.row);
        }
      } else {
        inlineEditorInput.focus();
      }

      this.startDoubleClickWindow(target);
    }
  };

  pointerDown = (world: Point, event: FederatedPointerEvent): boolean => {
    // In mobile mode, don't handle table clicks here - let PointerDown
    // handle tap detection so tables are only selected on tap, not press/drag
    if (isMobileMode()) {
      return false;
    }

    const isRightClick = event.button === 2 || (isMac && event.button === 0 && event.ctrlKey);
    let tableDown = content.cellsSheet.tables.pointerDown(world, isRightClick);
    if (!tableDown) return false;

    // Don't close the inline editor if we're editing a formula and the click
    // is on a table header (name or column) - we want to insert the table
    // reference into the formula instead. Also check cursorIsMoving for when
    // the user is actively changing a reference (clicking around to select
    // different cells/tables).
    const isEditingFormulaAndWantsRef =
      inlineEditorHandler.isEditingFormula() &&
      (inlineEditorHandler.cursorIsMoving || inlineEditorFormula.wantsCellRef());
    const isTableHeaderClick = tableDown.type === 'table-name' || tableDown.type === 'column-name';

    if (!isEditingFormulaAndWantsRef || !isTableHeaderClick) {
      events.emit('contextMenu', {});
    }

    if (tableDown.type === 'chart') {
      const target = { type: 'chart' as const, tableName: tableDown.table.name };
      if (this.isDoubleClickTarget(target)) {
        this.clearDoubleClickState();
        doubleClickCell({ column: tableDown.table.x, row: tableDown.table.y });
        return true;
      } else {
        sheets.sheet.cursor.selectTable(tableDown.table.name, undefined, false, false);
        this.startDoubleClickWindow(target);
      }
      return true;
    }

    if (isRightClick) {
      if (tableDown.column !== undefined) {
        const columnName = this.getColumnName(tableDown.table, tableDown.column);
        if (!columnName) {
          return true;
        }
        if (!sheets.sheet.cursor.isTableColumnSelected(tableDown.table.name, tableDown.column)) {
          sheets.sheet.cursor.selectTable(tableDown.table.name, columnName, false, false);
        }
      } else {
        sheets.sheet.cursor.selectTable(tableDown.table.name, undefined, false, false);
      }

      events.emit('contextMenu', {
        type: tableDown.type === 'column-name' ? ContextMenuType.TableColumn : ContextMenuType.Table,
        world,
        column: tableDown.table.x,
        row: tableDown.table.y,
        table: tableDown.table,
        selectedColumn: tableDown.column,
      });
      return true;
    }

    if (tableDown.type === 'run-code') {
      this.pointerDownRunCode(tableDown);
    } else if (tableDown.type === 'table-name') {
      this.pointerDownTableName(world, tableDown, event.shiftKey, event.ctrlKey || event.metaKey);
    } else if (tableDown.type === 'dropdown') {
      this.pointerDownDropdown(world, tableDown);
    } else if (tableDown.type === 'sort') {
      // tables doesn't have to do anything with sort; it's handled in TableColumnHeader
    } else if (tableDown.type === 'column-name') {
      this.pointerDownColumnName(world, tableDown, event.shiftKey, event.ctrlKey || event.metaKey);
    }
    return true;
  };

  pointerMove = (world: Point): boolean => {
    if (this.doubleClickTimeout) {
      this.clearDoubleClickState();
    }
    if (this.tableNameDown && hasPermissionToEditFile(gridSettings.getPermissions())) {
      // Only start a table move once the pointer has traveled past the drag
      // threshold from where it was pressed. A click's tiny jitter stays below
      // the threshold and falls through to a normal click (cleared in
      // pointerUp), so the cell-move UI no longer flashes on every click. The
      // press position and world are both in world coordinates, so divide the
      // pixel threshold by the viewport scale to keep it constant in screen
      // pixels regardless of zoom (matches PointerDown).
      const dx = world.x - this.tableNameDown.downWorld.x;
      const dy = world.y - this.tableNameDown.downWorld.y;
      const worldThreshold = TABLE_DRAG_THRESHOLD / pixiApp.viewport.scale.x;
      if (dx * dx + dy * dy >= worldThreshold * worldThreshold) {
        // Get all selected tables and filter out the primary (dragged) table
        const selectedTableNames = sheets.sheet.cursor.getSelectedTableNames();
        const additionalTables = selectedTableNames
          .filter((name) => name !== this.tableNameDown!.table.name)
          .map((name) => {
            const table = content.cellsSheet.tables.getTableFromName(name);
            if (table) {
              return {
                column: table.codeCell.x,
                row: table.codeCell.y,
                width: table.codeCell.w,
                height: table.codeCell.h,
                name: table.codeCell.name,
              };
            }
            return null;
          })
          .filter((t): t is NonNullable<typeof t> => t !== null);

        pixiApp.pointer.pointerCellMoving.tableMove(
          this.tableNameDown.column,
          this.tableNameDown.row,
          this.tableNameDown.point,
          this.tableNameDown.table.w,
          this.tableNameDown.table.h,
          this.tableNameDown.table.name,
          additionalTables.length > 0 ? additionalTables : undefined
        );
        this.tableNameDown = undefined;
      }
      return true;
    }
    const result = content.cellsSheet.tables.pointerMove(world);
    this.cursor = content.cellsSheet.tables.tableCursor;
    return result;
  };

  pointerUp = (): boolean => {
    if (this.tableNameDown) {
      this.tableNameDown = undefined;
      return true;
    }
    return false;
  };
}
